"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef } from "react";
const CHECKOUT_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

// Minimal ambient typing for the Razorpay checkout script — kept local to
// this hook since there's no official types package installed and we don't
// want `any` sprinkled across the rest of the codebase.
interface RazorpayHandlerResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}
export interface RazorpayCheckoutOptions {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  name?: string;
  description?: string;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  theme?: {
    color?: string;
  };
  handler: (response: RazorpayHandlerResponse) => void;
  modal?: {
    ondismiss?: () => void;
  };
}
interface RazorpayInstance {
  open: () => void;
}
declare global {
  interface Window {
    Razorpay?: new (options: RazorpayCheckoutOptions) => RazorpayInstance;
  }
}
function loadCheckoutScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Razorpay checkout can only run in the browser."));
  }
  if (window.Razorpay) {
    return Promise.resolve();
  }
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${CHECKOUT_SCRIPT_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay checkout script.")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = CHECKOUT_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout script."));
    document.body.appendChild(script);
  });
}
interface UseRazorpayCheckoutResult {
  openCheckout: (options: RazorpayCheckoutOptions) => Promise<void>;
}
export function useRazorpayCheckout() {
  const $ = _c(2);
  const loadPromiseRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async options => {
      if (!loadPromiseRef.current) {
        loadPromiseRef.current = loadCheckoutScript();
      }
      await loadPromiseRef.current;
      if (!window.Razorpay) {
        throw new Error("Razorpay checkout script did not initialize correctly.");
      }
      const instance = new window.Razorpay(options);
      instance.open();
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const openCheckout = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      openCheckout
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}